<template>
    <div>
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-medium text-gray-700 dark:text-gray-200">{{ title }}</h3>
        
        <div class="flex items-center space-x-2">
          <button
            @click="refresh"
            class="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            :disabled="loading"
          >
            <svg
              :class="{ 'animate-spin': loading }"
              class="w-5 h-5 text-gray-500 dark:text-gray-400"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          
          <button
            @click="toggleFilters"
            class="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg
              class="w-5 h-5 text-gray-500 dark:text-gray-400"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
          </button>
        </div>
      </div>
      
      <!-- Filters panel -->
      <div v-if="showFilters" class="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg mb-4 border dark:border-gray-700">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nota</label>
            <select
              v-model="filters.score"
              class="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm"
            >
              <option value="">Todas</option>
              <option value="promoters">Promotores (9-10)</option>
              <option value="neutrals">Neutros (7-8)</option>
              <option value="detractors">Detratores (0-6)</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Filial</label>
            <select
              v-model="filters.branch"
              class="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm"
            >
              <option value="">Todas</option>
              <option v-for="branch in branches" :key="branch.id" :value="branch.id">
                {{ branch.name }}
              </option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Período</label>
            <select
              v-model="filters.period"
              class="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm"
            >
              <option value="7d">Últimos 7 dias</option>
              <option value="30d">Últimos 30 dias</option>
              <option value="90d">Últimos 90 dias</option>
              <option value="all">Todo o período</option>
            </select>
          </div>
        </div>
        
        <div class="mt-4 flex justify-end">
          <button
            @click="resetFilters"
            class="px-4 py-2 bg-white dark:bg-gray-700 border dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 mr-2"
          >
            Limpar Filtros
          </button>
          <button
            @click="applyFilters"
            class="px-4 py-2 bg-blue-600 rounded-md shadow-sm text-sm font-medium text-white hover:bg-blue-700"
          >
            Aplicar
          </button>
        </div>
      </div>
      
      <!-- Feedback table -->
      <div class="bg-white dark:bg-gray-800 overflow-hidden rounded-lg border dark:border-gray-700">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead class="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Data
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Nota
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Filial
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Comentário
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              <tr v-if="loading || feedbacks.length === 0">
                <td colspan="5" class="px-6 py-8 text-center">
                  <div v-if="loading" class="flex justify-center">
                    <svg class="animate-spin h-6 w-6 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  <p v-else class="text-gray-500 dark:text-gray-400">Nenhum feedback encontrado</p>
                </td>
              </tr>
              
              <tr v-for="feedback in feedbacks" :key="feedback.id" v-else>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                  {{ formatDate(feedback.date) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span :class="`px-2 py-1 rounded-full text-xs font-medium ${getNpsClass(feedback.score)}`">
                    {{ feedback.score }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                  {{ feedback.branch }}
                </td>
                <td class="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 max-w-xs truncate">
                  {{ feedback.comment }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button @click="viewFeedback(feedback)" class="text-blue-600 hover:text-blue-800 dark:hover:text-blue-400 mr-3">
                    Ver
                  </button>
                  <button @click="replyToFeedback(feedback)" class="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200">
                    Responder
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <!-- Pagination -->
        <div class="px-6 py-3 border-t dark:border-gray-700 flex items-center justify-between">
          <div class="text-sm text-gray-500 dark:text-gray-400">
            Mostrando <span class="font-medium">{{ displayStart }}</span> a <span class="font-medium">{{ displayEnd }}</span> de <span class="font-medium">{{ totalItems }}</span> resultados
          </div>
          
          <div class="flex space-x-1">
            <button
              @click="prevPage"
              :disabled="currentPage === 1"
              :class="currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''"
              class="px-3 py-1 bg-white dark:bg-gray-700 border dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Anterior
            </button>
            <button
              @click="nextPage"
              :disabled="currentPage === totalPages"
              :class="currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''"
              class="px-3 py-1 bg-white dark:bg-gray-700 border dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Próxima
            </button>
          </div>
        </div>
      </div>
      
      <!-- Feedback Detail Modal -->
      <div v-if="selectedFeedback" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <!-- Background overlay -->
          <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" @click="selectedFeedback = null"></div>
          
          <!-- Modal panel -->
          <div class="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div class="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div class="sm:flex sm:items-start">
                <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900 sm:mx-0 sm:h-10 sm:w-10">
                  <svg class="h-6 w-6 text-blue-600 dark:text-blue-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100" id="modal-title">
                    Detalhes do Feedback
                  </h3>
                  <div class="mt-4 space-y-4">
                    <div>
                      <p class="text-sm text-gray-500 dark:text-gray-400">Data</p>
                      <p class="font-medium text-gray-900 dark:text-gray-100">{{ formatDate(selectedFeedback.date) }}</p>
                    </div>
                    
                    <div>
                      <p class="text-sm text-gray-500 dark:text-gray-400">Filial</p>
                      <p class="font-medium text-gray-900 dark:text-gray-100">{{ selectedFeedback.branch }}</p>
                    </div>
                    
                    <div>
                      <p class="text-sm text-gray-500 dark:text-gray-400">Nota</p>
                      <span :class="`px-2 py-1 rounded-full text-xs font-medium ${getNpsClass(selectedFeedback.score)}`">
                        {{ selectedFeedback.score }}
                      </span>
                    </div>
                    
                    <div>
                      <p class="text-sm text-gray-500 dark:text-gray-400">Comentário</p>
                      <p class="font-medium text-gray-900 dark:text-gray-100">{{ selectedFeedback.comment }}</p>
                    </div>
                    
                    <div v-if="selectedFeedback.contact">
                      <p class="text-sm text-gray-500 dark:text-gray-400">Contato</p>
                      <p class="font-medium text-gray-900 dark:text-gray-100">{{ selectedFeedback.contact }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button @click="replyToFeedback(selectedFeedback)" class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm">
                Responder
              </button>
              <button @click="selectedFeedback = null" class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { ref, computed, onMounted, watch } from 'vue';
  
  export default {
    name: 'RecentFeedbacks',
    props: {
      title: {
        type: String,
        default: 'Feedbacks Recentes'
      },
      companyId: {
        type: [String, Number],
        default: null
      },
      limit: {
        type: Number,
        default: 10
      }
    },
    setup(props, { emit }) {
      const loading = ref(false);
      const feedbacks = ref([]);
      const showFilters = ref(false);
      const selectedFeedback = ref(null);
      const currentPage = ref(1);
      const totalItems = ref(0);
      const itemsPerPage = ref(props.limit);
      
      // Filter state
      const filters = ref({
        score: '',
        branch: '',
        period: '30d'
      });
      
      // Branch options (would come from API)
      const branches = ref([
        { id: 1, name: 'Matriz - São Paulo' },
        { id: 2, name: 'Filial - Rio de Janeiro' },
        { id: 3, name: 'Filial - Belo Horizonte' },
        { id: 4, name: 'Filial - Brasília' }
      ]);
      
      // Pagination calculations
      const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage.value));
      const displayStart = computed(() => ((currentPage.value - 1) * itemsPerPage.value) + 1);
      const displayEnd = computed(() => Math.min(currentPage.value * itemsPerPage.value, totalItems.value));
      
      // Load feedback data
      const loadFeedbacks = async () => {
        loading.value = true;
        
        try {
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 800));
          
          // In a real app, you'd use axios or fetch to get data
          // const response = await axios.get(`/api/feedbacks`, { params: { ... } });
          
          // Mock data
          const mockData = [
            {
              id: 1,
              date: new Date('2025-02-27'),
              score: 10,
              branch: 'Matriz - São Paulo',
              comment: 'Excelente atendimento, equipe muito atenciosa e prestativa.',
              contact: 'cliente@exemplo.com'
            },
            {
              id: 2,
              date: new Date('2025-02-26'),
              score: 9,
              branch: 'Filial - Rio de Janeiro',
              comment: 'Produto com ótima qualidade, recomendo.',
              contact: '11999998888'
            },
            {
              id: 3,
              date: new Date('2025-02-26'),
              score: 7,
              branch: 'Filial - Belo Horizonte',
              comment: 'Bom serviço, mas poderia ser mais rápido.',
              contact: null
            },
            {
              id: 4,
              date: new Date('2025-02-25'),
              score: 3,
              branch: 'Matriz - São Paulo',
              comment: 'Problemas com o atendimento, demorou muito para resolver minha questão.',
              contact: 'cliente.insatisfeito@exemplo.com'
            },
            {
              id: 5,
              date: new Date('2025-02-24'),
              score: 10,
              branch: 'Filial - Brasília',
              comment: 'Superou todas as expectativas, excelente experiência!',
              contact: null
            }
          ];
          
          feedbacks.value = mockData;
          totalItems.value = 125; // Mock total count
        } catch (error) {
          console.error('Error loading feedbacks:', error);
          // In a real app, you might want to show an error message
        } finally {
          loading.value = false;
        }
      };
      
      // Format date for display
      const formatDate = (date) => {
        if (!date) return '';
        
        if (typeof date === 'string') {
          date = new Date(date);
        }
        
        return date.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      };
      
      // Get CSS class for NPS score
      const getNpsClass = (score) => {
        if (score >= 9) return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
        if (score >= 7) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      };
      
      // Filter actions
      const toggleFilters = () => {
        showFilters.value = !showFilters.value;
      };
      
      const resetFilters = () => {
        filters.value = {
          score: '',
          branch: '',
          period: '30d'
        };
      };
      
      const applyFilters = () => {
        currentPage.value = 1;
        loadFeedbacks();
        showFilters.value = false;
      };
      
      // Pagination actions
      const nextPage = () => {
        if (currentPage.value < totalPages.value) {
          currentPage.value++;
          loadFeedbacks();
        }
      };
      
      const prevPage = () => {
        if (currentPage.value > 1) {
          currentPage.value--;
          loadFeedbacks();
        }
      };
      
      // Refresh data
      const refresh = () => {
        currentPage.value = 1;
        loadFeedbacks();
      };
      
      // View feedback details
      const viewFeedback = (feedback) => {
        selectedFeedback.value = feedback;
      };
      
      // Reply to feedback
      const replyToFeedback = (feedback) => {
        emit('reply', feedback);
        // In a real app, you might navigate to a reply page or open a reply modal
        alert(`Responder ao feedback #${feedback.id}`);
      };
      
      // Load data when component mounts
      onMounted(() => {
        loadFeedbacks();
      });
      
      // Watch for company ID changes
      watch(() => props.companyId, () => {
        refresh();
      });
      
      return {
        loading,
        feedbacks,
        showFilters,
        filters,
        branches,
        selectedFeedback,
        currentPage,
        totalItems,
        totalPages,
        displayStart,
        displayEnd,
        formatDate,
        getNpsClass,
        toggleFilters,
        resetFilters,
        applyFilters,
        nextPage,
        prevPage,
        refresh,
        viewFeedback,
        replyToFeedback
      };
    }
  };
  </script>